/**
 * The one HTTP client for `/api/me/encryption/*`: the member's Next Steps
 * encryption state, devices, recovery wrapping and reset hold.
 *
 * Contract: hogc-one-api `docs/next-steps-encryption-api.md` (Phase 1b),
 * sections 4 to 6; spec `docs/plans/2026-09-02-next-steps-e2e-encryption.md`
 * section 6.6. Where the two disagree the deployed contract wins.
 *
 * Every function takes `auth` first — `{ token, deviceId }` — and sends
 * `Authorization: Bearer <token>` plus `X-Device-Id: <deviceId>`; the device
 * header is omitted when no id is known (a device that has not minted one yet).
 * URLs are built as `src/store/modules/courses.js` builds its own, from
 * `VUE_APP_API_URL`, so the global interceptors in `src/utils/interceptors/`
 * (falsy-bearer repair, 401 refresh) apply here too.
 *
 * No cryptography lives here. Signatures and wrappings arrive from the caller
 * (bundle-key signing and device wrapping are the crypto-primitives ticket);
 * this module only carries them to the wire and turns HTTP failures into one
 * error shape, `EncryptionApiError`, with a `code` a screen can branch on and
 * the API's own `apiCode` kept alongside it for a screen that wants to say
 * something sharper than the client code allows.
 */
import axios from 'axios';

/** The literal `POST /reset` requires, so a stray POST cannot start a hold. */
export const RESET_CONFIRMATION = 'START_OVER';
export const DEVICE_ID_HEADER = 'X-Device-Id';

/**
 * `MemberDevice.status` on the wire (contract section 4, `DeviceView`). Exported
 * so the store's getters and the two approval screens compare against one
 * spelling rather than each carrying its own string literal.
 *
 * `REVOKED` is the answer to BOTH a deny of a PENDING request and a revoke of an
 * APPROVED device (`device.service.ts:119-133`); `approvedAt` is what tells the
 * two apart on a row that carries it.
 */
export const DEVICE_STATUSES = Object.freeze({
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REVOKED: 'REVOKED',
});

/**
 * The API's machine-readable error codes, sent as `code` on the error body.
 *
 * Read INSTEAD of the English message wherever one arrives (`codeFor` below).
 * Each constant is spelt as its own wire string, so a screen can branch on a
 * single case — RECOVERY_VERSION_CONFLICT reads "your recovery moved on" where
 * ALREADY_ENROLLED reads "this is already set up" — even though both are one
 * `conflict` to `ENCRYPTION_ERROR_CODES`.
 *
 * The HTTP status and the English message are unchanged by the codes PR, so
 * nothing that reads either of them today breaks.
 */
export const API_ERROR_CODES = Object.freeze({
  /** 403 — a required operation arrived unsigned, OR `selfWrapped` arrived with no verified signature. */
  SIGNATURE_REQUIRED: 'SIGNATURE_REQUIRED',
  /** 403 — no live nonce. The ONLY retryable 403. */
  NO_LIVE_CHALLENGE: 'NO_LIVE_CHALLENGE',
  /** 403 — a signature was present and did not verify. */
  SIGNATURE_INVALID: 'SIGNATURE_INVALID',
  /** 503 — the promise row is unfilled; enrolment is refused. */
  ENCRYPTION_NOT_CONFIGURED: 'ENCRYPTION_NOT_CONFIGURED',
  /**
   * 403 or 404 — no key bundle. 404 from every route that needs one (and a
   * reset cancel); 403 from SignedOperationGuard, which completes a matured
   * "Start over" hold inside the request and then has no bundle to verify
   * against. NOT a signature failure, on either lane.
   */
  NOT_ENROLLED: 'NOT_ENROLLED',
  /** 404 — cancelReset with no hold. */
  NO_RESET_PENDING: 'NO_RESET_PENDING',
  /** 404 — no such device on this member. */
  DEVICE_NOT_FOUND: 'DEVICE_NOT_FOUND',
  /** 409 — enrol hit the unique violation ("Already enrolled, or that device id is in use"). */
  ALREADY_ENROLLED: 'ALREADY_ENROLLED',
  /** 409 — register: that device id belongs to another member. */
  DEVICE_ID_IN_USE: 'DEVICE_ID_IN_USE',
  /** 409 — register: the pending-device cap. */
  TOO_MANY_PENDING_DEVICES: 'TOO_MANY_PENDING_DEVICES',
  /** 409 — approve/deny/revoke on a device in the wrong status. */
  DEVICE_STATUS_MISMATCH: 'DEVICE_STATUS_MISMATCH',
  /** 409 — requestReset while a hold already exists. */
  RESET_ALREADY_PENDING: 'RESET_ALREADY_PENDING',
  /** 409 — setRecovery lost the compare-and-set on `recoveryVersion`. */
  RECOVERY_VERSION_CONFLICT: 'RECOVERY_VERSION_CONFLICT',
  /**
   * 409 — a recovery-code or passphrase write while a rotation is pending.
   *
   * NOT the version conflict, and the difference matters to the member:
   * "another device wrote first" invites a retry that works, this one invites a
   * retry that can never work until the rotation ends. `commitRotation`
   * promotes the wrapping captured at `rotate/begin`, and only the rotating
   * device holds MK2, so there is no version of this write that lands.
   */
  WRAPPING_LOCKED_BY_ROTATION: 'WRAPPING_LOCKED_BY_ROTATION',
  /** 409 — markMigrated while plaintext rows remain. */
  PLAINTEXT_REMAINS: 'PLAINTEXT_REMAINS',
  /**
   * 503 — the challenge store (Redis) did not answer within two seconds, on
   * `/challenge` and on every signed route. Nothing was consumed; retry later.
   */
  CHALLENGE_STORE_UNAVAILABLE: 'CHALLENGE_STORE_UNAVAILABLE',
});

/**
 * The three 403 messages SignedOperationGuard answers with
 * (signed-operation.guard.ts) — the FALLBACK discriminator, used only when the
 * error body carries no `code`. See `codeFor`.
 */
export const API_MESSAGES = Object.freeze({
  NO_LIVE_CHALLENGE: 'No live challenge: request one and sign again',
  SIGNATURE_REQUIRED: 'This operation needs a bundle-key signature',
  SIGNATURE_INVALID: 'The signature does not prove possession of the key',
});

/**
 * `EncryptionApiError.code` values.
 *
 * - `stale-nonce`: 403 with NO_LIVE_CHALLENGE. The challenge expired (two
 *   minutes) or was consumed by an earlier attempt. Retryable: request a fresh
 *   challenge and sign again (`withFreshChallenge` does this once).
 * - `signature-rejected`: any other 403 on these routes — SIGNATURE_REQUIRED,
 *   SIGNATURE_INVALID, or `selfWrapped` sent without a verified signature. The
 *   key is wrong, not the timing; a retry with the same key only burns a nonce.
 *   Never the missing-bundle case: that is `not-enrolled`.
 * - `not-enrolled`: NOT_ENROLLED, on 403 or 404 — this account has no notes
 *   key. Most often a "Start over" hold that matured and was completed inside
 *   the request. Not retryable, and emphatically not a key failure: the only
 *   true next step is to set Next Steps up again.
 * - `not-found`: 404 — the `courses-e2ee` flag is off (enrol only), no such
 *   device, or no hold to cancel.
 * - `conflict`: 409 — already enrolled, device id in use, five PENDING devices,
 *   device not in the expected status, recoveryVersion moved, plaintext rows
 *   remain, or a hold is already pending.
 * - `not-configured`: 503 — the promise row is unfilled; enrolment is refused.
 * - `unavailable`: 503 with CHALLENGE_STORE_UNAVAILABLE — the challenge store
 *   did not answer; nothing was consumed. Retryable, later.
 * - `rate-limited`: 429 — too many challenges requested (ten a minute per
 *   member and address). Retryable after `retryAfterSeconds`; never retried
 *   inside `withFreshChallenge`.
 * - `bad-request`: 400 — a DTO shape failure (costs no nonce), or a signed
 *   body this client refused to send because it carried no nonce.
 * - `unauthorized`: 401 — the session is gone; the 401 interceptor owns this.
 * - `network`: no response at all (offline, timeout, CORS). Retryable.
 * - `unknown`: any other status.
 */
export const ENCRYPTION_ERROR_CODES = Object.freeze({
  STALE_NONCE: 'stale-nonce',
  SIGNATURE_REJECTED: 'signature-rejected',
  NOT_ENROLLED: 'not-enrolled',
  NOT_FOUND: 'not-found',
  CONFLICT: 'conflict',
  NOT_CONFIGURED: 'not-configured',
  UNAVAILABLE: 'unavailable',
  RATE_LIMITED: 'rate-limited',
  BAD_REQUEST: 'bad-request',
  UNAUTHORIZED: 'unauthorized',
  NETWORK: 'network',
  UNKNOWN: 'unknown',
});

const RETRYABLE_CODES = new Set([
  ENCRYPTION_ERROR_CODES.STALE_NONCE,
  ENCRYPTION_ERROR_CODES.NETWORK,
  ENCRYPTION_ERROR_CODES.UNAVAILABLE,
  ENCRYPTION_ERROR_CODES.RATE_LIMITED,
]);

const STATUS_CODES = {
  400: ENCRYPTION_ERROR_CODES.BAD_REQUEST,
  401: ENCRYPTION_ERROR_CODES.UNAUTHORIZED,
  404: ENCRYPTION_ERROR_CODES.NOT_FOUND,
  409: ENCRYPTION_ERROR_CODES.CONFLICT,
  429: ENCRYPTION_ERROR_CODES.RATE_LIMITED,
  503: ENCRYPTION_ERROR_CODES.NOT_CONFIGURED,
};

/**
 * `API_ERROR_CODES` to the `code` a screen branches on. Several API codes share
 * one client code on purpose: the raw one survives as `apiCode` for a screen
 * that wants the distinction.
 *
 * A Map rather than an object literal so a body of `{ code: 'constructor' }`
 * cannot resolve to something off `Object.prototype` and be returned as a code.
 */
const CLIENT_CODE_BY_API_CODE = new Map([
  [API_ERROR_CODES.NO_LIVE_CHALLENGE, ENCRYPTION_ERROR_CODES.STALE_NONCE],
  [API_ERROR_CODES.SIGNATURE_REQUIRED, ENCRYPTION_ERROR_CODES.SIGNATURE_REJECTED],
  [API_ERROR_CODES.SIGNATURE_INVALID, ENCRYPTION_ERROR_CODES.SIGNATURE_REJECTED],
  [API_ERROR_CODES.ENCRYPTION_NOT_CONFIGURED, ENCRYPTION_ERROR_CODES.NOT_CONFIGURED],
  [API_ERROR_CODES.NOT_ENROLLED, ENCRYPTION_ERROR_CODES.NOT_ENROLLED],
  [API_ERROR_CODES.NO_RESET_PENDING, ENCRYPTION_ERROR_CODES.NOT_FOUND],
  [API_ERROR_CODES.DEVICE_NOT_FOUND, ENCRYPTION_ERROR_CODES.NOT_FOUND],
  [API_ERROR_CODES.ALREADY_ENROLLED, ENCRYPTION_ERROR_CODES.CONFLICT],
  [API_ERROR_CODES.DEVICE_ID_IN_USE, ENCRYPTION_ERROR_CODES.CONFLICT],
  [API_ERROR_CODES.TOO_MANY_PENDING_DEVICES, ENCRYPTION_ERROR_CODES.CONFLICT],
  [API_ERROR_CODES.DEVICE_STATUS_MISMATCH, ENCRYPTION_ERROR_CODES.CONFLICT],
  [API_ERROR_CODES.RESET_ALREADY_PENDING, ENCRYPTION_ERROR_CODES.CONFLICT],
  [API_ERROR_CODES.RECOVERY_VERSION_CONFLICT, ENCRYPTION_ERROR_CODES.CONFLICT],
  [API_ERROR_CODES.WRAPPING_LOCKED_BY_ROTATION, ENCRYPTION_ERROR_CODES.CONFLICT],
  [API_ERROR_CODES.PLAINTEXT_REMAINS, ENCRYPTION_ERROR_CODES.CONFLICT],
  [API_ERROR_CODES.CHALLENGE_STORE_UNAVAILABLE, ENCRYPTION_ERROR_CODES.UNAVAILABLE],
]);

/**
 * One failure shape for every route here.
 *
 * @property {number|null} status  HTTP status, or null when no response arrived
 * @property {string} code         one of ENCRYPTION_ERROR_CODES
 * @property {string|null} apiCode the API's own `code` verbatim (one of
 *   API_ERROR_CODES, or a code a later API added), or null when the body
 *   carried none — an API deployed before the codes PR, a network failure, or
 *   an error this module threw itself. `code` collapses several of these into
 *   one; read `apiCode` when a screen needs the single case, e.g. to tell
 *   RECOVERY_VERSION_CONFLICT from ALREADY_ENROLLED inside `conflict`.
 * @property {string} message      the API's message (a 400's array joined with '; ')
 * @property {boolean} retryable   true for `stale-nonce`, `network`, `unavailable` and `rate-limited`
 * @property {number|null} retryAfterSeconds the `Retry-After` header as seconds, when the API sent one
 * @property {string|null} route   the function that failed, e.g. 'approveDevice'
 * @property {Error|null} cause    the original axios error
 */
export class EncryptionApiError extends Error {
  constructor({
    status = null,
    code,
    apiCode = null,
    message,
    retryable = false,
    retryAfterSeconds = null,
    route = null,
    cause = null,
  }) {
    super(message);
    this.name = 'EncryptionApiError';
    this.status = status;
    this.code = code;
    this.apiCode = apiCode;
    this.retryable = retryable;
    this.retryAfterSeconds = retryAfterSeconds;
    this.route = route;
    this.cause = cause;
  }
}

/**
 * @param {unknown} error
 * @param {string} [code] when given, also require `error.code === code`
 * @returns {boolean}
 */
export function isEncryptionApiError(error, code) {
  if (!error || error.name !== 'EncryptionApiError') return false;
  return code === undefined || error.code === code;
}

/** `${VUE_APP_API_URL}/me/encryption`, read at call time so a test can point it anywhere. */
export function encryptionBaseUrl() {
  return `${process.env.VUE_APP_API_URL}/me/encryption`;
}

/**
 * `auth ?? {}` rather than a default parameter: a default catches `undefined`
 * but not `null`, and a null `auth` destructured raw is a TypeError that never
 * becomes an `EncryptionApiError` — the store would file it as
 * `{ code: 'unknown', retryable: false }`, indistinguishable from a real 500.
 * A missing token is not thrown on here on purpose: the falsy-bearer
 * interceptor (`src/utils/interceptors/auth-header.js`) substitutes the store
 * token or strips the header, and throwing locally would bypass that repair.
 */
function headersFor(auth) {
  const { token, deviceId } = auth ?? {};
  const headers = { Authorization: `Bearer ${token}` };
  if (deviceId !== null && deviceId !== undefined) headers[DEVICE_ID_HEADER] = deviceId;
  return headers;
}

/** A body with its `undefined` entries dropped, so an optional field is absent rather than null. */
function compact(body) {
  return Object.fromEntries(Object.entries(body).filter(([, value]) => value !== undefined));
}

function apiMessage(data, fallback) {
  const message = data && typeof data === 'object' ? data.message : undefined;
  if (Array.isArray(message)) return message.join('; ');
  if (typeof message === 'string' && message) return message;
  return fallback;
}

/** The API's `code`, but only when it is a non-empty string; null otherwise. */
function apiCodeOf(data) {
  const code = data && typeof data === 'object' ? data.code : undefined;
  return typeof code === 'string' && code ? code : null;
}

/**
 * The API's `code` first, its English message only as a fallback.
 *
 * The two lanes exist because the codes PR ships separately from this client
 * and BOTH orders of deploy have to work:
 *
 * - `apiCode` present and known — the answer, whatever the message says.
 * - `apiCode` absent (an API deployed before the codes PR) — the pre-code
 *   behaviour, unchanged: on a 403, an exact match on the English literal, else
 *   the status.
 * - `apiCode` present but unknown (a code a later API adds) — the status, not
 *   `unknown`: a 409 nobody has taught this client about is still a conflict.
 *
 * ⚠️ In the fallback lane the message literal is still load-bearing, and still
 * this client's own copy of a string no API test asserts (`API_MESSAGES`,
 * `signed-operation.guard.ts:20`): reword it there while an older client is in
 * the field and a stale two-minute nonce surfaces to that client as
 * `signature-rejected`, telling the member their key is wrong. Once the codes
 * PR is deployed everywhere the message stops deciding anything and the
 * fallback is dead weight, safe to delete.
 */
function codeFor(status, message, apiCode) {
  const known = apiCode === null ? undefined : CLIENT_CODE_BY_API_CODE.get(apiCode);
  if (known) return known;
  if (status === 403) {
    return message === API_MESSAGES.NO_LIVE_CHALLENGE
      ? ENCRYPTION_ERROR_CODES.STALE_NONCE
      : ENCRYPTION_ERROR_CODES.SIGNATURE_REJECTED;
  }
  return STATUS_CODES[status] || ENCRYPTION_ERROR_CODES.UNKNOWN;
}

/** `Retry-After` as whole seconds, or null when absent or not a number. */
function retryAfterOf(headers) {
  const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
  const seconds = Number(raw);
  return raw != null && Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

/**
 * The body of a signed operation, checked against the contract before any
 * request goes out: whenever `signature` is present, `nonce` — the exact string
 * `POST /challenge` returned and the signature was computed over — must be
 * present too (the API answers 400 otherwise, and the challenge store keeps one
 * key per nonce, so it has nothing else to look up); when `signature` is absent
 * (an unsigned register or reset), `nonce` is dropped with it. Refusing here
 * costs nothing; letting it reach the API would cost a live nonce for a
 * programming error.
 */
function signedBody(route, { signature, nonce, ...rest }) {
  if (signature === undefined || signature === null) return { ...rest };
  if (typeof nonce !== 'string' || !nonce) {
    throw new EncryptionApiError({
      code: ENCRYPTION_ERROR_CODES.BAD_REQUEST,
      message: `${route}: a signed body must echo the challenge nonce it was signed over`,
      route,
    });
  }
  return { ...rest, signature, nonce };
}

function toEncryptionApiError(error, route) {
  if (isEncryptionApiError(error)) return error;
  const response = error?.response;
  if (!response) {
    return new EncryptionApiError({
      status: null,
      code: ENCRYPTION_ERROR_CODES.NETWORK,
      message: error?.message || 'Network error',
      retryable: true,
      route,
      cause: error ?? null,
    });
  }
  const message = apiMessage(response.data, error.message);
  const apiCode = apiCodeOf(response.data);
  const code = codeFor(response.status, message, apiCode);
  return new EncryptionApiError({
    status: response.status,
    code,
    apiCode,
    message,
    retryable: RETRYABLE_CODES.has(code),
    retryAfterSeconds: retryAfterOf(response.headers),
    route,
    cause: error,
  });
}

/**
 * Send one request and unwrap `data`. `get` and `delete` take their body (if
 * any) through axios' `data` option; `post` and `put` take it positionally.
 */
async function send(route, method, url, { auth, data } = {}) {
  const headers = headersFor(auth);
  try {
    const response =
      method === 'get' || method === 'delete'
        ? await axios[method](url, data === undefined ? { headers } : { headers, data })
        : await axios[method](url, data, { headers });
    return response.data;
  } catch (error) {
    throw toEncryptionApiError(error, route);
  }
}

const devicePath = id => `${encryptionBaseUrl()}/devices/${encodeURIComponent(id)}`;

/**
 * `GET /api/me/encryption` — the member's encryption status.
 *
 * Never fails for an unenrolled member: answers `{ enrolled: false, ... }` with
 * every other field at its empty value, so it can be called unconditionally.
 * `thisDevice` is looked up by `X-Device-Id` and is the only device view that
 * carries `wrappedMk`; the call also bumps that device's `lastSeenAt`.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @returns {Promise<object>} EncryptionStatus (contract section 4)
 * @throws {EncryptionApiError} 401 unauthorized; network
 */
export function fetchEncryptionStatus(auth) {
  return send('fetchEncryptionStatus', 'get', encryptionBaseUrl(), { auth });
}

/**
 * `POST /api/me/encryption` — enrol: the bundle and the first device, which is
 * created APPROVED with no wrapping because it holds MK directly.
 *
 * The only route behind the `courses-e2ee` flag.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ keyId: string, signingKey: string, signingKeyWrapped: { iv: string, wrapped: string },
 *           device: { id: string, publicKey: string, name: string, platform: 'ios'|'android'|'web' } }} body
 * @returns {Promise<object>} the full EncryptionStatus for the newly enrolled member (201)
 * @throws {EncryptionApiError} 404 not-found (flag off); 503 not-configured (promise row unfilled);
 *   409 conflict (already enrolled, or device id in use); 400 bad-request
 */
export function enrol(auth, { keyId, signingKey, signingKeyWrapped, device }) {
  return send('enrol', 'post', encryptionBaseUrl(), {
    auth,
    data: { keyId, signingKey, signingKeyWrapped, device },
  });
}

/**
 * `POST /api/me/encryption/challenge` — a fresh nonce, live for two minutes,
 * one per member, consumed by the next signed call whether or not its
 * signature verifies.
 *
 * The body is checked rather than trusted: `withFreshChallenge` hands whatever
 * this returns straight to the signer, so a 200 with an empty or reshaped body
 * (a proxy, a future envelope wrapper) would sign over `"nonce":undefined`,
 * spend the live nonce and come back as `signature-rejected` — the one code
 * that means "the key is wrong" — for a body-shape problem.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @returns {Promise<string>} the nonce
 * @throws {EncryptionApiError} network; 401 unauthorized; `unknown` when the
 *   200 carried no nonce string
 */
export async function requestChallenge(auth) {
  const data = await send('requestChallenge', 'post', `${encryptionBaseUrl()}/challenge`, {
    auth,
  });
  const nonce = data?.nonce;
  if (typeof nonce !== 'string' || !nonce) {
    throw new EncryptionApiError({
      code: ENCRYPTION_ERROR_CODES.UNKNOWN,
      message: 'The challenge response carried no nonce',
      route: 'requestChallenge',
    });
  }
  return nonce;
}

/**
 * `POST /api/me/encryption/devices` — register this device.
 *
 * Without `signature` the device is created PENDING. With a bundle-key signature
 * it is created APPROVED, storing `selfWrapped` as its wrapping when one is
 * sent. Idempotent on the member's own id: a replay answers 201 with the
 * existing row, whatever its status — register never upgrades a row, so a
 * PENDING device that later obtains MK approves itself via `approveDevice`
 * instead.
 *
 * The signed fields depend on the body: `{ deviceId, publicKey }`, or
 * `{ deviceId, publicKey, selfWrappedHash }` when `selfWrapped` is sent
 * (contract section 5; the controller's `fields` callback is a ternary on
 * `body.selfWrapped`). A proven registration hashes its own wrapping into the
 * message for the same reason approve does: without it a valid signature could
 * carry a swapped `selfWrapped`.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ id: string, publicKey: string, name: string, platform: 'ios'|'android'|'web',
 *           selfWrapped?: { v: number, ephemeralPublicKey: string, iv: string, wrapped: string },
 *           signature?: string, nonce?: string }} device — `nonce` must accompany `signature` (contract: api PR #1092)
 * @returns {Promise<object>} the created or existing MemberDevice row (201)
 * @throws {EncryptionApiError} 403 signature-rejected (selfWrapped without a verified signature,
 *   or a bad signature) / stale-nonce; 409 conflict (five PENDING devices, or the id belongs to
 *   another member); 400 bad-request
 */
export async function registerDevice(
  auth,
  { id, publicKey, name, platform, selfWrapped, signature, nonce },
) {
  return send('registerDevice', 'post', `${encryptionBaseUrl()}/devices`, {
    auth,
    data: compact(
      signedBody('registerDevice', {
        id,
        publicKey,
        name,
        platform,
        selfWrapped,
        signature,
        nonce,
      }),
    ),
  });
}

/**
 * `PUT /api/me/encryption/devices/:id/approve` — approve a PENDING device with
 * MK wrapped to its public key. Signed over `approve` `{ deviceId, wrappedMkHash }`.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {string} id the pending device's id
 * @param {{ wrappedMk: { v: number, ephemeralPublicKey: string, iv: string, wrapped: string },
 *           signature: string, nonce: string }} body — `nonce` is the challenge the signature covers
 * @returns {Promise<object>} the approved MemberDevice row (200)
 * @throws {EncryptionApiError} 404 not-found; 409 conflict (not PENDING);
 *   403 stale-nonce / signature-rejected; 400 bad-request
 */
export async function approveDevice(auth, id, { wrappedMk, signature, nonce }) {
  return send('approveDevice', 'put', `${devicePath(id)}/approve`, {
    auth,
    data: signedBody('approveDevice', { wrappedMk, signature, nonce }),
  });
}

/**
 * `PUT /api/me/encryption/devices/:id/deny` — refuse a PENDING device (sets
 * REVOKED). Session-only on the deployed API: no signature, no body. A denied
 * id is never upgraded again; the device must mint a new identity to re-request.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {string} id
 * @returns {Promise<object>} the revoked MemberDevice row (200)
 * @throws {EncryptionApiError} 404 not-found; 409 conflict (not PENDING)
 */
export function denyDevice(auth, id) {
  return send('denyDevice', 'put', `${devicePath(id)}/deny`, { auth });
}

/**
 * `DELETE /api/me/encryption/devices/:id` — revoke an APPROVED device, clearing
 * its wrapping. Signed over `revoke` `{ deviceId }`. The body travels through
 * axios' `data` option because DELETE has no positional body argument.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {string} id
 * @param {{ signature: string, nonce: string }} body — `nonce` is the challenge the signature covers
 * @returns {Promise<object>} the revoked MemberDevice row, wrappedMk cleared (200)
 * @throws {EncryptionApiError} 404 not-found; 409 conflict (not APPROVED);
 *   403 stale-nonce / signature-rejected; 400 bad-request
 */
export async function revokeDevice(auth, id, { signature, nonce }) {
  return send('revokeDevice', 'delete', devicePath(id), {
    auth,
    data: signedBody('revokeDevice', { signature, nonce }),
  });
}

/**
 * `PUT /api/me/encryption/recovery` — set or replace the recovery wrapping.
 * Signed over `recovery` `{ recoveryWrappingHash, expectedVersion }`;
 * compare-and-set on `recoveryVersion`.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ recoveryWrapping: { salt: string, iv: string, wrapped: string },
 *           expectedVersion: number, signature: string, nonce: string }} body — `nonce` is the challenge the signature covers
 * @returns {Promise<object>} the updated MemberKeyBundle, recoveryVersion incremented (200)
 * @throws {EncryptionApiError} 409 conflict (expectedVersion moved); 404 not-found (unenrolled);
 *   403 stale-nonce / signature-rejected; 400 bad-request
 */
export async function setRecovery(auth, { recoveryWrapping, expectedVersion, signature, nonce }) {
  return send('setRecovery', 'put', `${encryptionBaseUrl()}/recovery`, {
    auth,
    data: signedBody('setRecovery', { recoveryWrapping, expectedVersion, signature, nonce }),
  });
}

/**
 * `PUT /api/me/encryption/passphrase` — set or replace the optional passphrase
 * wrapping (api#1125, decision 11). Signed over `passphrase`
 * `{ passphraseWrappingHash, expectedVersion }`; compare-and-set on
 * `passphraseVersion`.
 *
 * The wrapping is FLAT — `{ kdf, iterations, salt, iv, wrapped }` — because the
 * canonical hash the signature covers takes scalars only. The API refuses an
 * unknown `kdf` and fewer than 600,000 iterations, which is the only thing it
 * can check about a passphrase it never sees.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ passphraseWrapping: { kdf: string, iterations: number, salt: string, iv: string, wrapped: string },
 *           expectedVersion: number, signature: string, nonce: string }} body — `nonce` is the challenge the signature covers
 * @returns {Promise<object>} the updated MemberKeyBundle, passphraseVersion incremented (200)
 * @throws {EncryptionApiError} 409 conflict (expectedVersion moved); 404 not-found (unenrolled);
 *   403 stale-nonce / signature-rejected; 400 bad-request (unknown kdf, or too few iterations)
 */
export async function setPassphrase(
  auth,
  { passphraseWrapping, expectedVersion, signature, nonce },
) {
  return send('setPassphrase', 'put', `${encryptionBaseUrl()}/passphrase`, {
    auth,
    data: signedBody('setPassphrase', { passphraseWrapping, expectedVersion, signature, nonce }),
  });
}

/**
 * `DELETE /api/me/encryption/passphrase` — remove it (api#1125).
 *
 * Signed under `passphrase-remove`, an op of its own: this is the only
 * operation in the contract that takes a recovery path AWAY, so its signature
 * must never be presentable as anything else. Version-checked like setting one,
 * because a removal races a set — without it, a device removing the passphrase
 * it read a moment ago would silently delete one another device had just
 * written.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ expectedVersion: number, signature: string, nonce: string }} body
 * @returns {Promise<object>} the MemberKeyBundle with the wrapping cleared (200)
 * @throws {EncryptionApiError} 409 conflict (expectedVersion moved); 404 not-found (unenrolled);
 *   403 stale-nonce / signature-rejected; 400 bad-request
 */
export async function removePassphrase(auth, { expectedVersion, signature, nonce }) {
  return send('removePassphrase', 'delete', `${encryptionBaseUrl()}/passphrase`, {
    auth,
    data: signedBody('removePassphrase', { expectedVersion, signature, nonce }),
  });
}

/**
 * `POST /api/me/encryption/rotate/begin` — open a rotation (api#1134, #1138).
 *
 * Signed by the CURRENT bundle key over `rotate-begin`. The two wrapping
 * hashes are always in the message and null when the member has neither, so
 * the field set never changes with the account.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {object} body
 * @returns {Promise<object>} the MemberKeyBundle with the pending* columns set (200)
 * @throws {EncryptionApiError} 409 conflict (a rotation is already running, or
 *   `pendingKeyId` is the current one); 400 bad-request (the member has a
 *   recovery code or a passphrase and the re-wrap was not sent); 404
 *   not-found; 403 stale-nonce / signature-rejected
 */
export async function rotateBegin(auth, body) {
  return send('rotateBegin', 'post', `${encryptionBaseUrl()}/rotate/begin`, {
    auth,
    data: signedBody('rotateBegin', body),
  });
}

/**
 * ⚠️ `POST /api/me/encryption/rotate/commit` — THE DESTRUCTIVE ONE (api#1135).
 *
 * REVOKES EVERY DEVICE except the one that rotated and clears their
 * `wrappedMk`. That is "sign out all other devices", and it cannot be undone:
 * those devices come back only by being approved afresh.
 *
 * Signed by the PENDING key, because what it must prove is possession of the
 * generation being committed.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ pendingKeyId: string, signature: string, nonce: string }} body
 * @returns {Promise<object>} the MemberKeyBundle, promoted (200)
 * @throws {EncryptionApiError} 409 conflict (no rotation, a different one, or
 *   rows still on the old key — the count is in the message)
 */
export async function rotateCommit(auth, { pendingKeyId, keep, signature, nonce }) {
  return send('rotateCommit', 'post', `${encryptionBaseUrl()}/rotate/commit`, {
    auth,
    // `keep` (api#1156) only when there is something to keep: absent means what
    // commit has always meant, and the signature covers it only when sent.
    data: signedBody('rotateCommit', {
      pendingKeyId,
      ...(keep?.length ? { keep } : {}),
      signature,
      nonce,
    }),
  });
}

/**
 * `POST /api/me/encryption/rotate/abort` — drop the pending generation
 * (api#1135). Nothing is revoked and no version moves.
 *
 * Signed by the pending key, under an op of its OWN: the same key signing the
 * same field under one shared op would make an abort signature interchangeable
 * with a commit one (decision 49).
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ pendingKeyId: string, signature: string, nonce: string }} body
 * @returns {Promise<object>} the MemberKeyBundle with the pending* columns cleared (200)
 * @throws {EncryptionApiError} 409 conflict (no rotation, a different one, or
 *   rows still on the NEW key — dropping it would strand them)
 */
/**
 * `POST /api/me/encryption/rotate/abort-begin` — enter the ABORTING state
 * (api#1148, spec 6.12 step 4a).
 *
 * ⚠️ WHAT IT DOES IS INVERT THE WRITE GATE. From here the API accepts writes on
 * the COMMITTED generation and refuses the pending one, and `rotation.remaining`
 * counts rows still on the pending generation. That is what lets the re-seal
 * walk run backwards; without it an abort is impossible the moment one row has
 * moved, because nothing may write under the committed key.
 *
 * One-way and idempotent: there is no route back to rotating, and asking twice
 * is the same answer rather than a return trip.
 *
 * Signed by the PENDING key under its own op, distinct from `rotate-abort` —
 * they carry identical fields, so a shared op would let this signature be
 * replayed as the finisher, or the finisher's as this (decision 49).
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ pendingKeyId: string, signature: string, nonce: string }} body
 * @returns {Promise<object>} the MemberKeyBundle, now marked aborting (200)
 * @throws {EncryptionApiError} 409 when there is no rotation, or a different one
 */
export async function rotateAbortBegin(auth, { pendingKeyId, signature, nonce }) {
  return send('rotateAbortBegin', 'post', `${encryptionBaseUrl()}/rotate/abort-begin`, {
    auth,
    data: signedBody('rotateAbortBegin', { pendingKeyId, signature, nonce }),
  });
}

export async function rotateAbort(auth, { pendingKeyId, signature, nonce }) {
  return send('rotateAbort', 'post', `${encryptionBaseUrl()}/rotate/abort`, {
    auth,
    data: signedBody('rotateAbort', { pendingKeyId, signature, nonce }),
  });
}

/**
 * `GET /api/me/encryption/rows` — the member's rows still sealed under one key
 * generation (api#1128). Ids and revisions only, never payloads.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ kid: string, cursor?: string, limit?: number }} params
 * @returns {Promise<{ rows: Array<{ type: string, id: string, revision: number }>,
 *   nextCursor?: string, remaining: number }>}
 * @throws {EncryptionApiError} 400 bad-request (no `kid`, a non-integer limit,
 *   or a malformed cursor)
 */
export function fetchRotationRows(auth, { kid, cursor, limit } = {}) {
  // Built into the URL rather than passed as `params`: `send` takes `data`,
  // and axios treats a GET's second argument as config — a `params` key handed
  // to it here would be sent as a body and dropped, leaving a request with no
  // `kid` that the API answers 400 for.
  const query = new URLSearchParams({
    ...(kid ? { kid } : {}),
    ...(cursor ? { cursor } : {}),
    ...(limit ? { limit: String(limit) } : {}),
  });
  return send('fetchRotationRows', 'get', `${encryptionBaseUrl()}/rows?${query}`, { auth });
}

/**
 * `POST /api/me/encryption/migrated` — assert that no plaintext rows remain;
 * the server counts before setting `migratedAt`. Session-only; idempotent once set.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @returns {Promise<object>} the MemberKeyBundle with migratedAt set (200)
 * @throws {EncryptionApiError} 404 not-found (unenrolled); 409 conflict (plaintext remains,
 *   per-table count in the message)
 */
export function markMigrated(auth) {
  return send('markMigrated', 'post', `${encryptionBaseUrl()}/migrated`, { auth });
}

/**
 * `POST /api/me/encryption/reset` — "Start over".
 *
 * With a bundle-key signature over `reset` `{}`: erases the bundle, every
 * device and every note, highlight, tag and blank answer now, answering
 * `{ enrolled: false, erased }`. Without: starts a seven-day hold, answering the
 * MemberKeyBundle with `resetRequestedAt`/`resetAt` set and `resetRequestedBy`
 * from `X-Device-Id`.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ signature?: string, nonce?: string }} [body] — `nonce` must accompany `signature`
 * @returns {Promise<object>} (200)
 * @throws {EncryptionApiError} 404 not-found (unenrolled; unproven path); 409 conflict (a hold is
 *   already pending; unproven path); 403 stale-nonce / signature-rejected (signature present but
 *   invalid); 400 bad-request
 */
export async function requestReset(auth, { signature, nonce } = {}) {
  return send('requestReset', 'post', `${encryptionBaseUrl()}/reset`, {
    auth,
    data: compact(
      signedBody('requestReset', { confirmation: RESET_CONFIRMATION, signature, nonce }),
    ),
  });
}

/**
 * `DELETE /api/me/encryption/reset` — cancel a pending hold. Signed over
 * `cancel` `{}`, so a session thief cannot cancel a genuine reset.
 *
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {{ signature: string, nonce: string }} body — `nonce` is the challenge the signature covers
 * @returns {Promise<object>} the MemberKeyBundle with the reset columns cleared (200)
 * @throws {EncryptionApiError} 404 not-found (unenrolled, or no hold pending);
 *   403 stale-nonce / signature-rejected; 400 bad-request
 */
export async function cancelReset(auth, { signature, nonce }) {
  return send('cancelReset', 'delete', `${encryptionBaseUrl()}/reset`, {
    auth,
    data: signedBody('cancelReset', { signature, nonce }),
  });
}
